<template>
  <Teleport to="body">
    <div class="pointer-events-none fixed inset-x-0 bottom-4 z-toast flex flex-col items-center gap-2 px-4" dir="rtl">
      <TransitionGroup name="toast">
        <div
          v-for="t in toasts"
          :key="t.id"
          class="pointer-events-auto flex max-w-md items-start gap-2 rounded-2xl px-4 py-3 text-sm shadow-lg ring-1"
          :class="toneClass(t.type)"
          role="status"
          @click="dismiss(t.id)"
        >
          <component :is="icon(t.type)" class="mt-0.5 h-4 w-4 shrink-0" />
          <span>{{ t.message }}</span>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup>
import { h } from 'vue'
import { useToast } from '@/composables/useToast'

/**
 * ══ the second toast system ════════════════════════════════════════════════════════════════════════
 *
 * §5.8 lists this file for deletion — `ToastBar` has 32 adopters and this one is the rival. It is
 * still here because §11's deletion rule is `rg -l` first: it has FOUR live importers (`App.vue`,
 * `composables/useToast.js`, `StateBlock.vue`, `AdminManagementPage.vue`), so deleting it is a
 * migration, not a delete, and it belongs to whoever owns `App.vue`. Reported, not taken.
 *
 * Converted to the token set meanwhile (§3.3, §3.8, §2.1): `z-[100]` -> `z-toast` — the arbitrary
 * value happened to equal the scale's own toast layer, which is exactly the coincidence that lets a
 * layer drift the next time someone edits it; `rounded-xl` -> `rounded-2xl` (a floating surface, not
 * a control); and `leading-5` deleted from the message span — the line box comes from the token, which
 * is 14/24 as of §14.1, and a 20px box clipped nothing only because these messages are one line today.
 */

const { toasts, dismiss } = useToast()

const toneClass = (type) => ({
  success: 'bg-success-50 text-success-700 ring-success-200 dark:bg-success-500/10 dark:text-success-300 dark:ring-success-500/30',
  error: 'bg-error-50 text-error-700 ring-error-200 dark:bg-error-500/10 dark:text-error-300 dark:ring-error-500/30',
  info: 'bg-brand-50 text-brand-700 ring-brand-200 dark:bg-brand-500/10 dark:text-brand-300 dark:ring-brand-500/30',
}[type] || 'bg-gray-50 text-gray-700 ring-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:ring-gray-700')

// Tiny inline SVG icons so we don't depend on an icon set being imported here.
const stroke = { fill: 'none', stroke: 'currentColor', 'stroke-width': '2', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }
const svg = (children) => h('svg', { viewBox: '0 0 24 24', ...stroke }, children)
const icon = (type) => () => {
  if (type === 'success') return svg([h('path', { d: 'M20 6 9 17l-5-5' })])
  if (type === 'error') return svg([h('circle', { cx: 12, cy: 12, r: 10 }), h('path', { d: 'M12 8v4' }), h('path', { d: 'M12 16h.01' })])
  return svg([h('circle', { cx: 12, cy: 12, r: 10 }), h('path', { d: 'M12 16v-4' }), h('path', { d: 'M12 8h.01' })])
}
</script>

<style scoped>
.toast-enter-active,
.toast-leave-active { transition: all 0.25s ease; }
.toast-enter-from { opacity: 0; transform: translateY(8px); }
.toast-leave-to { opacity: 0; transform: translateY(8px); }
</style>
